import React, { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorMessageFromError } from "../../components/ErrorMessage";
import { useApp } from "../../hooks/useApp";
import { ClientDto } from "../../model/ClientDto";
import { ClientDelete, ClientListRequest, ClientSaveRequest, ClientUpdatedRequest } from "../../services/client/ClientService";
import { useSidebarData } from "../global/SidebarDataContext";
import { getMessageTemplateRequest } from "../../services/templates/MessageTemplatesService";
import { TemplateDto } from "../../model/TemplatesDto";

const VendorsDataContext = createContext();

const VendorsDataContextProvider = ({ children }) => {
    const [listVendors, setListVendors] = useState([]);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [clientIdToDelete, setClientIdToDelete] = useState('');
    const [formData, setFormData] = useState({});
    const [company, setCompany] = useState(' ');
    const [countryPhone, setCountryPhone] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [isFirstRenderRowsPerPage, setIsFirstRenderRowsPerPage] = useState(true);
    const [page, setPage] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [vendorActive, setVendorActive] = useState(true);
    const [templatesTwilio, setTemplatesTwilio] = useState([]);
    const [templatesEmail, setTemplatesEmail] = useState([]);

    const {
        setSuccessMsg,
        setErrorMsg,
        setLoading,
        loading,
        messageUser,
        authenticate,
        authInfo
    } = useApp();

    const { notificationsWS } = useSidebarData();
    const navigate = useNavigate();

    const handleChangePage = (event, newPage) => { setPage(newPage); };
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); };

    const loadVendorsTimeout = () => { setTimeout(() => loadVendors(), 100); };

    useEffect(() => {
        if (authenticate && authInfo) { loadVendorsTimeout(); }
    }, [page, authenticate, authInfo]);

    useEffect(() => {
        if (isFirstRenderRowsPerPage) setIsFirstRenderRowsPerPage(false);
        else { if (page == 0) loadVendorsTimeout(); else setPage(0); }
    }, [rowsPerPage]);

    const handleCancel = async () => {
        navigate(-1);
        setCountryPhone(null);
    };

    const loadVendors = async () => {
        try {
            setLoading(true);
            const statusVendor = (authInfo?.userRole?.id === 1 ? (vendorActive ? 'ACTIVE' : 'DELETED') : 'ACTIVE');
            let responseVendorsList = await ClientListRequest(
                2, page, rowsPerPage, company, authInfo.id, authInfo.userRole.id,
                statusVendor, countryPhone ? countryPhone.id : 0, searchInput
            );
            setTotalItems(responseVendorsList.totalElements);
            const vendors = responseVendorsList.content.map(clientData => {
                return new ClientDto(
                    clientData.id,
                    clientData.name,
                    clientData.cellPhone,
                    clientData.email,
                    clientData.prospect,
                    clientData.jobTitle,
                    clientData.company,
                    clientData.company_name,
                    clientData.status,
                    clientData.countryPhone,
                    clientData.lastContactDate,
                    clientData.lastContactChannel,
                    clientData.customerStatusId,
                    clientData.customerStatusName,
                    clientData.leadTagName,
                    clientData.leadTagId,
                    clientData.enableAiAssistant,
                    clientData.leadTags || [],
                    clientData.leadTagIds || [],
                    clientData.meetingDate,
                    clientData.proposalDate,
                    clientData.notes,
                    clientData.value,
                    clientData.userId,
                    clientData.ownerName
                );
            });
            setListVendors(vendors);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            setErrorMsg(getErrorMessageFromError(error));
        }
    };

    const onClickMarkChatUnread = (params) => {
        if (messageUser.some(item => item.client.id === params.row.id)) {
            navigate('/dashboard/whatsapp', { state: params.row });
        }
    };

    const openDeleteConfirmation = (clientId) => { setClientIdToDelete(clientId); setShowDeleteConfirmation(true); };
    const closeDeleteConfirmation = () => { setClientIdToDelete(null); setShowDeleteConfirmation(false); };
    const handleDeleteConfirmed = async () => { closeDeleteConfirmation(); if (clientIdToDelete) { await handleDelete(clientIdToDelete); } };

    const handleFormSubmit = async (values) => {
        if (!countryPhone || !countryPhone.id || countryPhone.id == 0) {
            setErrorMsg("País es un campo requerido.");
            return;
        }
        values.countryPhone = countryPhone;
        try {
            const input = { ...values };
            setLoading(true);
            if (values.id) {
                await ClientUpdatedRequest(input);
                setSuccessMsg("Editado con éxito");
            } else {
                await ClientSaveRequest(input);
                setSuccessMsg("Creado con éxito");
            }
            setLoading(false);
            if (page == 0) loadVendorsTimeout(); else setPage(0);
            handleCancel();
        } catch (error) {
            let errorMessage = getErrorMessageFromError(error);
            if (!errorMessage) errorMessage = "Error desconocido al procesar la solicitud";
            setErrorMsg(errorMessage);
            setLoading(false);
        }
    };

    const handleDelete = async (clientId) => {
        try {
            setLoading(true);
            await ClientDelete(clientId);
            setSuccessMsg("Eliminado con éxito");
            if (page == 0) loadVendorsTimeout(); else setPage(0);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    const convertToLead = async (vendorId) => {
        try {
            setLoading(true);
            await ClientUpdatedRequest({ id: vendorId, prospect: 1 });
            setSuccessMsg("Convertido a Lead con éxito");
            if (page == 0) loadVendorsTimeout(); else setPage(0);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            setErrorMsg(getErrorMessageFromError(error));
        }
    };

    const convertToClient = async (vendorId) => {
        try {
            setLoading(true);
            await ClientUpdatedRequest({ id: vendorId, prospect: 0 });
            setSuccessMsg("Convertido a Cliente con éxito");
            if (page == 0) loadVendorsTimeout(); else setPage(0);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            setErrorMsg(getErrorMessageFromError(error));
        }
    };

    // Notifications WebSocket
    useEffect(() => {
        if (notificationsWS?.messageIncoming && notificationsWS?.client?.id) {
            const sortedVendors = listVendors.slice().sort((a, b) => {
                const aHasNewMessage = notificationsWS.client.id === a.id;
                const bHasNewMessage = notificationsWS.client.id === b.id;
                if (aHasNewMessage && !bHasNewMessage) return -1;
                if (!aHasNewMessage && bHasNewMessage) return 1;
                return 0;
            });
            setListVendors(sortedVendors);
        }
    }, [notificationsWS]);

    const handleChangeVendorActive = (event) => { setVendorActive(event.target.checked); };

    const loadTemplates = async () => {
        try {
            const whatsappTwilio = { type: 'WHATSAPP_TWILIO', active: true, page: 0, size: 100, authorized: true };
            const responseDataTwilio = await getMessageTemplateRequest(whatsappTwilio);
            const templateInstancesTwilio = responseDataTwilio.content.map(templateData =>
                new TemplateDto(
                    templateData.id, templateData.template, templateData.name, templateData.type,
                    templateData.active, templateData.authorized, templateData.contentSidTwilio, templateData.nameTemplateTwilio
                )
            );
            setTemplatesTwilio(templateInstancesTwilio);

            const emailParams = { type: 'EMAIL', active: true, page: 0, size: 100 };
            const responseEmail = await getMessageTemplateRequest(emailParams);
            setTemplatesEmail(
                (responseEmail.content || []).map(t => ({
                    id: t.id, name: t.name, subject: t.subject || '', template: t.template,
                }))
            );
        } catch (error) { }
    };

    useEffect(() => {
        if (page == 0 && authInfo?.userRole?.id != undefined) setTimeout(() => loadVendors(), 100);
        else setPage(0);
    }, [vendorActive]);

    useEffect(() => { loadTemplates(); }, []);

    return (
        <VendorsDataContext.Provider value={{
            loading,
            listVendors,
            isEditing,
            formData,
            loadVendors,
            setIsEditing,
            showDeleteConfirmation,
            clientIdToDelete,
            onClickMarkChatUnread,
            openDeleteConfirmation,
            closeDeleteConfirmation,
            handleDeleteConfirmed,
            handleDelete,
            handleFormSubmit,
            handleCancel,
            setFormData,
            company,
            setCompany,
            countryPhone,
            setCountryPhone,
            searchInput,
            setSearchInput,
            setSuccessMsg,
            setErrorMsg,
            page,
            setPage,
            rowsPerPage,
            handleChangePage,
            handleChangeRowsPerPage,
            totalItems,
            setTotalItems,
            authenticate,
            authInfo,
            handleChangeVendorActive,
            vendorActive,
            templatesTwilio,
            templatesEmail,
            convertToLead,
            convertToClient,
        }}>
            {children}
        </VendorsDataContext.Provider>
    );
};

export { VendorsDataContext, VendorsDataContextProvider };
